import type { UsercentricsObject } from './usercentrics.types';

const DEFAULT_SETTINGS_ID = 'Tr_3B1GDkkB1q2';
const UC_LOADER_ID = 'usercentrics-cmp';
const UC_TCF_STUB_ID = 'usercentrics-cmp-tcf-stub';

type EnsureUsercentricsV3LoadedOptions = {
  settingsId?: string;
  suppressCmpDisplay?: boolean;
};

declare global {
  interface Window {
    UC_UI_DOMAINS?: {
      crossDomainConsentSharingIFrame: string;
    };
    UC_UI_SUPPRESS_CMP_DISPLAY?: boolean;
    __ucCmp?: UsercentricsObject;
  }

  interface WindowEventMap {
    UC_UI_INITIALIZED: Event;
  }
}

let loadUsercentricsPromise: Promise<void> | undefined;

const appendScript = ({
  id,
  src,
  attributes = {},
}: {
  id: string;
  src: string;
  attributes?: Record<string, string>;
}): void => {
  if (document.getElementById(id)) {
    return;
  }

  const script = document.createElement('script');
  script.id = id;
  script.src = src;
  script.async = true;

  for (const [name, value] of Object.entries(attributes)) {
    script.setAttribute(name, value);
  }

  document.head.appendChild(script);
};

export const ensureUsercentricsV3Loaded = ({
  settingsId = DEFAULT_SETTINGS_ID,
  suppressCmpDisplay = false,
}: EnsureUsercentricsV3LoadedOptions = {}): Promise<void> => {
  // Skip loading on the server.
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (suppressCmpDisplay) {
    window.UC_UI_SUPPRESS_CMP_DISPLAY = true;
  }

  // Reuse the already initialized CMP.
  if (window.__ucCmp) {
    return Promise.resolve();
  }

  // Create the shared bootstrap promise only once.
  if (!loadUsercentricsPromise) {
    loadUsercentricsPromise = new Promise((resolve) => {
      const handleInitialized = (): void => {
        // Wait until the V3 API is available.
        if (!window.__ucCmp) {
          return;
        }

        window.removeEventListener('UC_UI_INITIALIZED', handleInitialized);
        resolve();
      };

      window.addEventListener('UC_UI_INITIALIZED', handleInitialized);

      window.UC_UI_DOMAINS = {
        crossDomainConsentSharingIFrame:
          'https://www.xing.com/cross-domain-bridge.html',
      };

      appendScript({
        id: UC_TCF_STUB_ID,
        src: 'https://web.cmp.usercentrics.eu/tcf/stub.js',
      });

      appendScript({
        id: UC_LOADER_ID,
        src: 'https://web.cmp.usercentrics.eu/ui/loader.js',
        attributes: {
          'data-settings-id': settingsId,
        },
      });
    });
  }

  return loadUsercentricsPromise;
};
